import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { AlertTriangle, ArrowDown, ArrowUp, Download, Loader2 } from 'lucide-react'

import { RefreshButton } from '@/components/RefreshButton'

import { api } from '@/api/client'
import { useFileDownload } from '@/lib/useFileDownload'
import { useToast } from '@/stores/toast'
import { useTabField } from '@/stores/tabState'
import { MultiPartnerPicker } from '@/components/MultiPartnerPicker'
import { SearchBox } from '@/components/SearchBox'
import { DateRangeBar } from '@/components/DateRangeBar'
import type { SalesReportResponse } from '@/types/models'
import { cn, formatNum, isFractionalCountUnit, isLargeQty } from '@/lib/utils'

type SortKey =
  | 'reg' | 'po_seq' | 'date' | 'order_no' | 'item_code' | 'item_name'
  | 'partner_code' | 'partner_name' | 'qty' | 'unit_price' | 'total'

interface SortState { key: SortKey; order: 'asc' | 'desc' }

const TAX_OPTIONS = [
  ['all',     '전체'],
  ['exempt',  '면세'],
  ['taxable', '과세'],
  ['zero',    '영세'],
] as const

interface ColDef {
  key: string
  label: string
  align?: 'left' | 'right' | 'center'
  sort?: SortKey
  px: number          // 고정 열너비(px) — table-layout:fixed + 가상화 안정성용
}

const COLUMNS: ColDef[] = [
  { key: '_rownum',       label: 'No.',          align: 'center', sort: 'po_seq', px: 52 },
  { key: 'order_date',    label: '일자',         sort: 'date',         px: 96 },
  { key: 'item_code',     label: '품목코드',     sort: 'item_code',    px: 110 },
  { key: 'item_name',     label: '품명',         sort: 'item_name', align: 'left', px: 200 },
  { key: 'spec',          label: '규격(원산지)', px: 110 },
  { key: 'unit',          label: '단위',         align: 'center', px: 52 },
  { key: 'qty',           label: '수량',         align: 'right', sort: 'qty',        px: 84 },
  { key: 'unit_price',    label: '단가',         align: 'right', sort: 'unit_price', px: 96 },
  { key: 'supply_amount', label: '공급가액',     align: 'right', px: 110 },
  { key: 'vat_amount',    label: '부가세',       align: 'right', px: 96 },
  { key: 'total',         label: '합계금액',     align: 'right', sort: 'total',      px: 112 },
  { key: 'tax_type_label',label: '과세구분',     align: 'center', px: 72 },
  { key: 'order_no',      label: '문서번호',     sort: 'order_no',     px: 150 },
  { key: 'partner_code',  label: '거래처코드',   sort: 'partner_code', px: 88 },
  { key: 'partner_name',  label: '거래처명',     sort: 'partner_name', px: 160 },
  { key: 'is_prepaid',    label: '이월/선납',    align: 'center', px: 76 },
  { key: 'subdivide',     label: '소분',         align: 'center', px: 56 },
  { key: 'note',          label: '비고1(업장)',  align: 'left', px: 130 },
  { key: 'note2',         label: '비고2(품목)',  align: 'left', px: 130 },
]

// 가상화 파라미터 — 화면엔 보이는 행 + 여유분만 DOM 에 올린다(대량 행 OOM 방지).
const ROW_H = 34
const OVERSCAN = 10
const VIEWPORT_H = 640

export function SalesReportTab() {
  const push = useToast((s) => s.push)
  const { downloading, download } = useFileDownload()

  // 기간·필터·정렬은 탭을 오가도 유지(판매월보 스코프).
  const K = 'sales-report'
  const [dateFrom, setDateFrom] = useTabField(K, 'dateFrom', '')   // 기본 빈 날짜(직접 지정)
  const [dateTo, setDateTo] = useTabField(K, 'dateTo', '')
  const [partners, setPartners] = useTabField<number[]>(K, 'partners', [])
  const [taxType, setTaxType] = useTabField<string>(K, 'taxType', 'all')
  const [search, setSearch] = useTabField(K, 'search', '')
  // 기본 정렬 = 문서번호(order_no) 순. 문서번호는 날짜별로 재발번되므로, 날짜를 합쳐도
  // (예: 28일 작업을 27일로 이동) 문서번호 순서대로 깔끔히 나열되어 섞임이 없다.
  const [sort, setSort] = useTabField<SortState>(K, 'sort', { key: 'order_no', order: 'asc' })
  const [purchaseMode, setPurchaseMode] = useTabField(K, 'purchaseMode', false)  // 구매장 기준(선납 제외 + 소분 분할)

  function buildParams(): URLSearchParams {
    const p = new URLSearchParams({
      from: dateFrom, to: dateTo,
      sort: sort.key, order: sort.order,
    })
    if (partners.length) p.set('partner', partners.join(','))
    if (taxType && taxType !== 'all') p.set('tax_type', taxType)
    if (search) p.set('q', search)
    if (purchaseMode) p.set('purchase_mode', '1')
    return p
  }

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['sales-report', dateFrom, dateTo, partners, taxType, search, sort.key, sort.order, purchaseMode],
    queryFn: () => api<SalesReportResponse>(`/api/sales-report?${buildParams()}`),
    enabled: !!dateFrom && !!dateTo,   // 기간 지정 전엔 조회 안 함
  })

  function toggleSort(key: SortKey) {
    setSort((s) =>
      s.key === key
        ? { key, order: s.order === 'asc' ? 'desc' : 'asc' }
        : { key, order: 'asc' },
    )
  }

  async function downloadExcel() {
    if (!data?.lines.length) {
      push('내보낼 데이터가 없습니다', 'warn'); return
    }
    const filename = `${purchaseMode ? '판매월보_구매장기준' : '판매월보'}_${dateFrom}_${dateTo}.xlsx`
    await download(`/api/sales-report/export?${buildParams()}`, filename)
  }

  const lines = data?.lines ?? []
  const totals = data?.totals
  const totalCount = data?.total_count ?? lines.length

  // ── 가상화(windowing) ──
  const scrollRef = useRef<HTMLDivElement>(null)
  const [scrollTop, setScrollTop] = useState(0)
  const [viewportH, setViewportH] = useState(VIEWPORT_H)

  // 새 조회 결과가 오면 스크롤을 맨 위로 리셋 + 뷰포트 높이 재측정.
  useEffect(() => {
    const el = scrollRef.current
    if (el) { el.scrollTop = 0; setViewportH(el.clientHeight || VIEWPORT_H) }
    setScrollTop(0)
  }, [data])

  function onScroll() {
    const el = scrollRef.current
    if (!el) return
    setScrollTop(el.scrollTop)
    if (el.clientHeight && el.clientHeight !== viewportH) setViewportH(el.clientHeight)
  }

  const total = lines.length
  const startIdx = Math.max(0, Math.floor(scrollTop / ROW_H) - OVERSCAN)
  const endIdx = Math.min(total, Math.ceil((scrollTop + viewportH) / ROW_H) + OVERSCAN)
  const visible = lines.slice(startIdx, endIdx)
  const topPad = startIdx * ROW_H
  const botPad = Math.max(0, (total - endIdx) * ROW_H)

  return (
    <div className="max-w-[1600px] mx-auto p-6">
      <div className="mb-4 flex justify-end">
        <button onClick={downloadExcel} disabled={downloading || !lines.length}
                className="bg-white hover:bg-slate-50 disabled:opacity-50 border border-slate-300 text-slate-700 text-sm font-semibold rounded-lg px-3 py-1.5 inline-flex items-center gap-1.5">
          {downloading ? <Loader2 className="w-4 h-4 animate-spin"/> : <Download className="w-4 h-4"/>}
          엑셀 다운로드
        </button>
      </div>

      <DateRangeBar from={dateFrom} to={dateTo} onFrom={setDateFrom} onTo={setDateTo}>
        <RefreshButton busy={isFetching} onClick={() => { void refetch() }} />
        <div className="flex-1 min-w-[200px]">
          <MultiPartnerPicker value={partners} onChange={setPartners} showGroups/>
        </div>
        <select value={taxType} onChange={(e) => setTaxType(e.target.value)}
                className="px-2 py-1.5 border border-slate-300 rounded-lg text-sm bg-white">
          {TAX_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <SearchBox value={search} onChange={setSearch}
                   placeholder="품목·거래처·출고번호 검색" className="flex-1 min-w-[200px]"/>
        <label className="inline-flex items-center gap-1.5 text-sm text-slate-600 whitespace-nowrap cursor-pointer select-none"
               title="선납 라인 제외 + 소분 라인 N줄 분할 (구매장과 동일 기준)">
          <input type="checkbox" checked={purchaseMode}
                 onChange={(e) => setPurchaseMode(e.target.checked)}/>
          구매장 기준
        </label>
        <span className="text-xs text-slate-400 tabular-nums whitespace-nowrap">{formatNum(totalCount)}건</span>
      </DateRangeBar>

      {data?.truncated && (
        <div className="mb-2 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5"/>
          <span>
            총 <b>{formatNum(data.total_count)}</b>건 중 상위 <b>{formatNum(data.max_rows)}</b>건만 화면에 표시합니다.
            날짜·거래처·검색으로 범위를 좁히거나, <b>엑셀 다운로드</b>로 전체를 받으세요.
            (합계는 전체 기준으로 정확합니다.)
          </span>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div ref={scrollRef} onScroll={onScroll} className="overflow-auto" style={{ maxHeight: VIEWPORT_H }}>
        <table className="w-full text-sm" style={{ tableLayout: 'fixed' }}>
          <colgroup>
            {COLUMNS.map((c) => <col key={c.key} style={{ width: c.px }} />)}
          </colgroup>
          <thead className="bg-slate-50 text-xs uppercase text-slate-500 tracking-wide sticky top-0 z-10">
            <tr>
              {COLUMNS.map((c) => {
                const active = c.sort && sort.key === c.sort
                const alignCls =
                  c.align === 'right' ? 'text-right' :
                  c.align === 'center' ? 'text-center' : 'text-left'
                return (
                  <th key={c.key}
                      title={c.sort === 'po_seq' ? '클릭 시 발주서 순서로 정렬 (안다즈 등 업장 분할 발주 검수용)' : undefined}
                      className={cn('px-3 py-2.5 font-medium whitespace-nowrap bg-slate-50', alignCls,
                        c.sort && 'cursor-pointer hover:text-slate-700 select-none')}
                      onClick={() => c.sort && toggleSort(c.sort)}>
                    <span className="inline-flex items-center gap-0.5">
                      {c.label}
                      {active && (sort.order === 'asc'
                        ? <ArrowUp className="w-3 h-3"/>
                        : <ArrowDown className="w-3 h-3"/>)}
                    </span>
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {topPad > 0 && (
              <tr aria-hidden style={{ height: topPad }}><td colSpan={COLUMNS.length} className="p-0"/></tr>
            )}
            {visible.map((l, i) => (
              <tr key={`${l.id}-${startIdx + i}`} style={{ height: ROW_H }}
                  className={cn('border-t border-slate-100 hover:bg-emerald-50/40',
                    l.is_carryover && 'bg-sky-50')}>
                <td className="px-2 py-1.5 text-center tabular-nums text-slate-400 text-xs overflow-hidden">{startIdx + i + 1}</td>
                <td className="px-3 py-1.5 tabular-nums text-slate-600 whitespace-nowrap overflow-hidden">{l.order_date}</td>
                <td className="px-3 py-1.5 font-mono text-xs tabular-nums text-slate-500 truncate">{l.item_code}</td>
                <td className="px-3 py-1.5 font-medium text-slate-900 truncate" title={l.item_name}>{l.item_name}</td>
                <td className="px-3 py-1.5 text-slate-500 text-xs truncate" title={l.spec}>{l.spec}</td>
                <td className="px-3 py-1.5 text-center text-slate-500 text-xs truncate">{l.unit}</td>
                <td className="px-3 py-1.5 text-right tabular-nums overflow-hidden">
                  <span className={cn(isLargeQty(l.qty) ? 'bg-red-200 text-red-700 rounded px-1'
                    : isFractionalCountUnit(l.unit, l.qty) && 'bg-yellow-200 rounded px-1')}>{formatNum(l.qty)}</span>
                </td>
                <td className="px-3 py-1.5 text-right tabular-nums overflow-hidden">{formatNum(l.unit_price)}</td>
                <td className="px-3 py-1.5 text-right tabular-nums overflow-hidden">{formatNum(l.supply_amount)}</td>
                <td className="px-3 py-1.5 text-right tabular-nums text-slate-500 overflow-hidden">{formatNum(l.vat_amount)}</td>
                <td className="px-3 py-1.5 text-right tabular-nums font-semibold overflow-hidden">{formatNum(l.total)}</td>
                <td className="px-3 py-1.5 text-center overflow-hidden">
                  <span className={cn(
                    'text-xs px-1.5 py-0.5 rounded-full font-semibold',
                    l.tax_type === 'taxable' ? 'bg-blue-100 text-blue-700' :
                    l.tax_type === 'zero' ? 'bg-purple-100 text-purple-700' :
                    'bg-slate-100 text-slate-600')}>
                    {l.tax_type_label}
                  </span>
                </td>
                <td className="px-3 py-1.5 font-mono text-xs tabular-nums whitespace-nowrap overflow-hidden text-ellipsis">
                  <Link to="/main/delivery"
                        search={{ order: l.order_id, from: dateFrom, to: dateTo }}
                        className="text-blue-600 hover:text-blue-800 hover:underline">
                    {l.order_no}
                  </Link>
                </td>
                <td className="px-3 py-1.5 font-mono text-xs tabular-nums text-slate-500 truncate">{l.partner_code}</td>
                <td className="px-3 py-1.5 text-slate-700 truncate" title={l.partner_name}>{l.partner_name}</td>
                <td className="px-2 py-1.5 text-center overflow-hidden whitespace-nowrap">
                  {l.is_carryover && (
                    <span className="text-[10px] px-1 py-0.5 rounded-full bg-sky-100 text-sky-700 font-semibold">이월</span>
                  )}
                  {l.sale_type_label && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 font-semibold">{l.sale_type_label}</span>
                  )}
                  {l.is_prepaid && (
                    <span className="text-xs px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold">선납</span>
                  )}
                </td>
                <td className="px-3 py-1.5 text-center tabular-nums text-slate-600 text-xs overflow-hidden">
                  {l.subdivide ? l.subdivide : ''}
                </td>
                <td className="px-3 py-1.5 text-slate-600 text-xs truncate" title={l.note}>{l.note}</td>
                <td className="px-3 py-1.5 text-slate-600 text-xs truncate" title={l.note2}>{l.note2}</td>
              </tr>
            ))}
            {botPad > 0 && (
              <tr aria-hidden style={{ height: botPad }}><td colSpan={COLUMNS.length} className="p-0"/></tr>
            )}
            {!isLoading && !lines.length && (
              <tr><td colSpan={COLUMNS.length} className="text-center text-slate-400 py-12 text-sm">
                조건에 맞는 출고 라인이 없습니다.
              </td></tr>
            )}
            {isLoading && (
              <tr><td colSpan={COLUMNS.length} className="text-center text-slate-400 py-12 text-sm">
                <Loader2 className="w-5 h-5 animate-spin inline-block mr-1"/> 불러오는 중...
              </td></tr>
            )}
          </tbody>
          {totals && lines.length > 0 && (
            <tfoot className="bg-emerald-50 font-bold border-t-2 border-emerald-200 sticky bottom-0 z-10">
              <tr>
                <td colSpan={6} className="px-3 py-2.5 text-right text-xs text-emerald-800 uppercase tracking-wide bg-emerald-50">합  계</td>
                <td className="px-3 py-2.5 text-right tabular-nums text-emerald-900 bg-emerald-50">{formatNum(totals.qty)}</td>
                <td className="bg-emerald-50"></td>
                <td className="px-3 py-2.5 text-right tabular-nums text-emerald-900 bg-emerald-50">{formatNum(totals.supply_amount)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums text-emerald-800 bg-emerald-50">{formatNum(totals.vat_amount)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums text-emerald-900 text-base bg-emerald-50">{formatNum(totals.total)}</td>
                <td colSpan={6} className="bg-emerald-50"></td>
              </tr>
            </tfoot>
          )}
        </table>
        </div>
      </div>
    </div>
  )
}
